import { apiClient } from "@/config/api.config";

/**
 * Service for handling contact form operations
 */
class ContactService {
  /**
   * Submit contact form data
   * @param {Object} formData - Contact form data
   * @param {string} formData.name - User's full name
   * @param {string} formData.email - User's email address
   * @param {string} formData.mobile - User's mobile number (optional)
   * @param {string} formData.subject - Message subject
   * @param {string} formData.message - Message content
   * @returns {Promise} - Promise with the response data
   */
  async submitContactForm(formData) {
    const response = await apiClient.post(
      "/appointments/contact/submit",
      formData
    );
    // Ensure response contains the message data
    if (response.data && response.data.success) {
      return {
        ...response.data,
        data: {
          ...response.data.data,
          message: formData.message, // Include the message in response
        },
      };
    }
    throw new Error(response.data?.message || "Failed to submit message");
  }

  /**
   * Get all contacts with pagination and filters
   * @param {number} page - Current page number
   * @param {number} limit - Number of contacts per page
   * @param {Object} filters - Filter criteria
   * @returns {Promise} - Promise with the response data
   */
  async getAllContacts(page = 1, limit = 10, filters = {}) {
    try {
      // Build query string
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...filters,
      });

      const response = await apiClient.get(
        `/appointments/contact?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get contact by ID
   * @param {string} id - Contact ID
   * @returns {Promise} - Promise with the response data
   */
  async getContactById(id) {
    try {
      const response = await apiClient.get(`/appointments/contact/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  // async getUserContact() {
  //   try {
  //     // Get user ID from local storage
  //     const userData = localStorage.getItem("user");
  //     if (!userData) {
  //       throw new Error("User not authenticated");
  //     }

  //     const user = JSON.parse(userData);
  //     if (!user._id) {
  //       throw new Error("User ID not found");
  //     }

  //     // Call the API with the user ID
  //     const response = await apiClient.get(`/user/contact/${user._id}`);
  //     return response.data;
  //   } catch (error) {
  //     throw error;
  //   }
  // }
  // async getUserContact() {
  //   try {
  //     const userString = localStorage.getItem("user");
  //     if (!userString) throw new Error("User not authenticated");

  //     const user = JSON.parse(userString);
  //     const userId = user.id || user._id;
  //     if (!userId) throw new Error("User ID not found");

  //     // Use the new endpoint
  //     const response = await apiClient.get(
  //       `/appointments/contact/user/${userId}`
  //     );
  //     return response.data;
  //   } catch (error) {
  //     throw error;
  //   }
  // }
  /**
   * Update contact status and details
   * @param {string} id - Contact ID
   * @param {Object} updateData - Data to update
   * @param {string} updateData.status - New status
   * @param {string} updateData.adminNotes - Admin notes
   * @param {string} updateData.assignedTo - User assigned to
   * @returns {Promise} - Promise with the response data
   */
  async updateContactStatus(id, updateData) {
    try {
      const response = await apiClient.patch(
        `/appointments/contact/${id}`,
        updateData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send a reply to a contact
   * @param {string} id - Contact ID
   * @param {Object} replyData - Reply data
   * @param {string} replyData.replySubject - Reply subject
   * @param {string} replyData.replyMessage - Reply message content
   * @returns {Promise} - Promise with the response data
   */
  async replyToContact(id, replyData) {
    try {
      const response = await apiClient.post(
        `/appointments/contact/${id}/reply`,
        replyData
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete contact
   * @param {string} id - Contact ID
   * @returns {Promise} - Promise with the response data
   */
  async deleteContact(id) {
    try {
      const response = await apiClient.delete(`/appointments/contact/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  // Get all contacts for current user
  async getUserContacts() {
    try {
      const response = await apiClient.get("/appointments/user/contacts", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      // Additional client-side filtering if needed
      const userEmail = JSON.parse(localStorage.getItem("user"))?.email;
      const filteredContacts = userEmail
        ? response.data.data.filter((c) => c.email === userEmail)
        : response.data.data;

      return {
        success: true,
        data: filteredContacts,
      };
    } catch (error) {
      throw error;
    }
  }

  async getUserContactById(id) {
    try {
      const response = await apiClient.get(
        `/appointments/user/contacts/${id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async submitUserReply(contactId, { message, subject }) {
    try {
      const response = await apiClient.post(
        `/appointments/user/contacts/${contactId}/reply`,
        { message, subject },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  //admin support
  /**
   * Get all contacts with pagination and filters
   * @param {number} page - Current page number
   * @param {number} limit - Number of contacts per page
   * @param {Object} filters - Filter criteria
   * @returns {Promise} - Promise with the response data
   */
  async getAllSupport(page = 1, limit = 10, filters = {}) {
    try {
      // Build query string
      const queryParams = new URLSearchParams({
        page,
        limit,
        ...filters,
      });

      const response = await apiClient.get(
        `/support/admin/tickets?${queryParams.toString()}`
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  async getSupportById(id) {
    try {
      const response = await apiClient.get(`/support/admin/tickets/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  async replyToSupport(id, replyData) {
    try {
      const response = await apiClient.post(
        `/support/admin/tickets/${id}/reply`,
        {
          message: replyData.replyMessage,
          subject: replyData.replySubject || `Re: ${replyData.originalSubject}`,
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  //usersupport
  async getUserSupport(page = 1, limit = 10) {
    try {
      const response = await apiClient.get(
        `/support/tickets?page=${page}&limit=${limit}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      // Return the docs array from the response data structure
      return {
        success: true,
        data: response.data.data.docs,
        pagination: {
          totalDocs: response.data.data.totalDocs,
          totalPages: response.data.data.totalPages,
          page: response.data.data.page,
          hasNextPage: response.data.data.hasNextPage,
          hasPrevPage: response.data.data.hasPrevPage,
        },
      };
    } catch (error) {
      throw error;
    }
  }
  async createSupportMessage(formData) {
    try {
      const response = await apiClient.post("/support/messages", {
        subject: formData.subject,
        description: formData.message,
        priority: formData.priority || "medium",
      });

      return {
        ...response.data.data,
        message: response.data.data.description, // Use description instead of replies[0]
        replies: [
          {
            message: response.data.data.description,
            sentBy: "user",
            sentAt: response.data.data.createdAt,
          },
        ], // Create a replies array manually
        _id: response.data.data.id,
      };
    } catch (error) {
      throw error;
    }
  }
  async submitUserSupportReply(ticketId, { message, subject }) {
    try {
      const response = await apiClient.post(
        `/support/tickets/${ticketId}/reply`,
        { message, subject },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  async updateTicketStatus(ticketId, status) {
    try {
      const response = await apiClient.put(
        `/support/admin/tickets/${ticketId}/status`,
        { status },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      if (error.response) {
        throw new Error(
          error.response.data.message || "Failed to update status"
        );
      }
      throw error;
    }
  }
}

export default new ContactService();
